// services/exportService.js
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDate, formatAmount } from '../utils/formatters';

// Service pour l'export des contrats
const exportService = {
  // 📊 EXPORT EXCEL
  exportToExcel: (contracts, filters = {}) => {
    try {
      console.log(`📊 Export Excel de ${contracts.length} contrats`);

      // Préparer les données pour Excel
      const excelData = contracts.map((contract, index) => ({
        'N°': index + 1,
        'Agence': contract.agency || '',
        'N° Contrat': contract.contractNumber || '',
        'N° Dossier': contract.dossier || '',
        'Poids Bijou': contract.jewelWeight || 0,
        'CDVA': contract.cdvaWeight || 0,
        'CDD': contract.cddWeight || 0,
        'Statut': exportService.calculateStatus(contract),
        'État': contract.etatContrat || 'Aucun',
        'Date Création': formatDate(contract.creationDate),
        'Date Limite': formatDate(contract.limitDate),
        'Montant Décaissement': contract.disbursementAmount || 0,
        'Montant Prévu': contract.expectedAmount || 0,
        'Montant Perçu': contract.receivedAmount || 0,
        'Reste': contract.remainingAmount || 0,
        'Dernier Versement': formatDate(contract.lastPaymentDate),
        'Téléphone': contract.phone1 || '',
        'Client': contract.client || '',
        'Date Clôture': formatDate(contract.closingDate),
        'Remise': contract.discount || 0,
        'Remarque': contract.remark || ''
      }));

      // Créer le workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Ajuster la largeur des colonnes
      const colWidths = [
        { wch: 5 },   // N°
        { wch: 12 },  // Agence
        { wch: 15 },  // N° Contrat
        { wch: 12 },  // N° Dossier
        { wch: 12 },  // Poids Bijou
        { wch: 10 },  // CDVA
        { wch: 10 },  // CDD
        { wch: 25 },  // Statut
        { wch: 12 },  // État
        { wch: 15 },  // Date Création
        { wch: 15 },  // Date Limite
        { wch: 18 },  // Montant Décaissement
        { wch: 15 },  // Montant Prévu
        { wch: 15 },  // Montant Perçu
        { wch: 12 },  // Reste
        { wch: 18 },  // Dernier Versement
        { wch: 15 },  // Téléphone
        { wch: 25 },  // Client
        { wch: 15 },  // Date Clôture
        { wch: 12 },  // Remise
        { wch: 15 }   // Remarque
      ];

      ws['!cols'] = colWidths;

      // Ajouter la feuille au workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Contrats');

      // Générer le nom de fichier avec la date
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10);
      let fileName = `contrats_${dateStr}`;

      // Ajouter les filtres au nom si présents
      if (filters.agency) fileName += `_${filters.agency}`;
      if (filters.year) fileName += `_${filters.year}`;
      if (filters.month) fileName += `_${filters.month}`;
      if (filters.statusFilter) fileName += `_${filters.statusFilter}`;

      fileName += '.xlsx';

      // Télécharger le fichier
      XLSX.writeFile(wb, fileName);

      console.log(`✅ Export Excel terminé: ${fileName}`);
      return { success: true, fileName };

    } catch (error) {
      console.error('❌ Erreur export Excel:', error);
      return { success: false, error: error.message };
    }
  },

  // 🖨️ EXPORT PDF
  exportToPDF: (contracts, filters = {}, totals = {}) => {
    try {
      console.log(`🖨️ Export PDF de ${contracts.length} contrats`);

      // Créer le document PDF
      const doc = new jsPDF('l', 'mm', 'a4'); // Format paysage
      
      // En-tête du document
      doc.setFontSize(16);
      doc.setTextColor(40);
      doc.text('LISTE DES CONTRATS', 20, 20);

      // Informations sur les filtres
      doc.setFontSize(10);
      doc.setTextColor(100);
      let yPos = 30;

      if (filters.agency || filters.year || filters.month || filters.statusFilter) {
        doc.text('Filtres appliqués:', 20, yPos);
        yPos += 5;

        if (filters.agency) {
          doc.text(`• Agence: ${filters.agency}`, 25, yPos);
          yPos += 4;
        }
        if (filters.year) {
          doc.text(`• Année: ${filters.year}`, 25, yPos);
          yPos += 4;
        }
        if (filters.month) {
          doc.text(`• Mois: ${filters.month}`, 25, yPos);
          yPos += 4;
        }
        if (filters.statusFilter) {
          const statusLabel = filters.statusFilter === 'depassement' ? 'Dépassement (non vendus)' : filters.statusFilter === 'vendu' ? 'Vendus' : 'Recouvrements (en cours)';
          doc.text(`Filtre : ${statusLabel}`, 14, yPos);
          yPos += 4;
        }
        yPos += 5;
      }

      doc.text(`Date d'export: ${new Date().toLocaleDateString('fr-FR')}`, 20, yPos);
      doc.text(`Nombre de contrats: ${contracts.length}`, 120, yPos);
      yPos += 10;

      // Préparer les données pour le tableau
      const tableData = contracts.map((contract, index) => [
        index + 1,
        contract.agency || '',
        contract.contractNumber || '',
        (contract.jewelWeight || 0).toFixed(2),
        (contract.cdvaWeight || 0).toFixed(2),
        (contract.cddWeight || 0).toFixed(2),
        exportService.calculateStatus(contract),
        formatDate(contract.creationDate),
        formatAmount(contract.expectedAmount),
        formatAmount(contract.receivedAmount),
        formatAmount(contract.remainingAmount),
        contract.client || '',
        contract.remark || ''
      ]);

      // En-têtes du tableau
      const headers = [
        'N°', 'Agence', 'Contrat', 'P.Bijou', 'CDVA', 'CDD', 
        'Statut', 'Création', 'Prévu', 'Perçu', 'Reste', 'Client', 'Rmq'
      ];

      // Configurer et dessiner le tableau
      autoTable(doc, {
        head: [headers],
        body: tableData,
        startY: yPos,
        styles: {
          fontSize: 7,
          cellPadding: 1,
        },
        headStyles: {
          fillColor: [60, 130, 200],
          textColor: 255,
          fontSize: 8,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 10 },  // N°
          1: { cellWidth: 20 },                    // Agence
          2: { cellWidth: 25 },                    // Contrat
          3: { halign: 'right', cellWidth: 15 },   // P.Bijou
          4: { halign: 'right', cellWidth: 15 },   // CDVA
          5: { halign: 'right', cellWidth: 15 },   // CDD
          6: { cellWidth: 35 },                    // Statut
          7: { cellWidth: 20 },                    // Création
          8: { halign: 'right', cellWidth: 20 },   // Prévu
          9: { halign: 'right', cellWidth: 20 },   // Perçu
          10: { halign: 'right', cellWidth: 20 },  // Reste
          11: { cellWidth: 30 },                   // Client
          12: { halign: 'center', cellWidth: 15 }  // Rmq
        },
        margin: { left: 10, right: 10 },
        tableWidth: 'auto',
        theme: 'striped'
      });

      // Ajouter les totaux si disponibles
      if (totals && Object.keys(totals).length > 0) {
        const finalY = doc.previousAutoTable.finalY + 10;
        
        doc.setFontSize(10);
        doc.setTextColor(40);
        doc.text('TOTAUX:', 20, finalY);
        
        doc.setFontSize(9);
        doc.setTextColor(100);
        let totalsY = finalY + 8;
        
        if (totals.jewelWeight) {
          doc.text(`Poids bijou total: ${totals.jewelWeight.toFixed(2)}`, 20, totalsY);
          totalsY += 5;
        }
        if (totals.expectedAmount) {
          doc.text(`Montant prévu total: ${formatAmount(totals.expectedAmount)}`, 20, totalsY);
          totalsY += 5;
        }
        if (totals.receivedAmount) {
          doc.text(`Montant perçu total: ${formatAmount(totals.receivedAmount)}`, 20, totalsY);
          totalsY += 5;
        }
        if (totals.remainingAmount) {
          doc.text(`Reste total: ${formatAmount(totals.remainingAmount)}`, 20, totalsY);
        }
      }

      // Générer le nom de fichier
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10);
      let fileName = `contrats_${dateStr}`;

      if (filters.agency) fileName += `_${filters.agency}`;
      if (filters.year) fileName += `_${filters.year}`;
      if (filters.month) fileName += `_${filters.month}`;
      if (filters.statusFilter) fileName += `_${filters.statusFilter}`;

      fileName += '.pdf';

      // Sauvegarder le PDF
      doc.save(fileName);

      console.log(`✅ Export PDF terminé: ${fileName}`);
      return { success: true, fileName };

    } catch (error) {
      console.error('❌ Erreur export PDF:', error);
      return { success: false, error: error.message };
    }
  },

  // 🔧 Fonction utilitaire pour calculer le statut (réplique du frontend)
  calculateStatus: (contract) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Vérifier si le contrat est vendu
    if (contract.remark && contract.remark.toLowerCase().includes("vendu")) {
      return "Contrat vendu";
    }

    if (contract.remark === "R" || contract.remark === "NR") {
      if (contract.closingDate && contract.creationDate) {
        const diffTime = Math.abs(
          new Date(contract.closingDate) - new Date(contract.creationDate)
        );
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return `Rachat sous : ${diffDays} jour(s)`;
      }
    }

    if (contract.limitDate) {
      const limitDate = new Date(contract.limitDate);
      limitDate.setHours(0, 0, 0, 0);
      const diffTime = limitDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        return "Dépassement d'échéance";
      } else {
        return `Reste : ${diffDays} jour(s) avant terme`;
      }
    }

    return contract.status || "État non défini";
  }
};

export default exportService;